import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useUser } from "../context/UserContext";
import { hasAnyGrant } from "../helpers/permissions";
import { docTarget, type DocType } from "../helpers/docRoutes";

export type DocLinkType = DocType;

interface DocLinkProps {
  docType: DocLinkType;
  /** Id of the referenced document — nothing is rendered without it. */
  id?: number | null;
  /** Doc no. from the preloaded relation; falls back to `#id` when the relation wasn't loaded. */
  docNo?: string | null;
  /** Rendered when the document reference is absent (most of these links are optional). */
  fallback?: ReactNode;
}

/**
 * A reference to another document is never a dead end: wherever a document names its
 * source (a PR's MRQ, a GRN's PO or requisition) that reference links to it. Falls back to
 * plain text when the user cannot read that document type, and swallows the click so a
 * link inside a clickable table row doesn't also open the row.
 */
export function DocLink({ docType, id, docNo, fallback = null }: DocLinkProps) {
  const { user } = useUser();
  if (!id) return <>{fallback}</>;

  const label = docNo || `#${id}`;
  const target = docTarget(docType, id);
  if (!target || !hasAnyGrant(user, target.permission)) return <>{label}</>;
  return (
    <Link to={target.path} onClick={(e) => e.stopPropagation()}>
      {label}
    </Link>
  );
}

// A link has no text of its own to walk in a CSV export (helpers/csv.ts) — the doc no. is it.
DocLink.csvText = ({ id, docNo, fallback }: DocLinkProps) => (id ? docNo || `#${id}` : fallback);
